import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight, Diamond } from "lucide-react";
import CarCard from "../components/CarCard";
import { MaskLine, Reveal } from "../components/Reveal";
import { useLang } from "../i18n/LanguageContext";
import { api, waLink } from "../lib/api";

const HERO_IMG =
  "https://images.unsplash.com/photo-1614200179396-2bdb77ebf81b?crop=entropy&cs=srgb&fm=jpg&q=85";
const SHOWROOM_IMG =
  "https://images.unsplash.com/photo-1764013290175-2b76e9a00b2e?crop=entropy&cs=srgb&fm=jpg&q=85";
const DETAIL_IMG =
  "https://images.unsplash.com/photo-1627454819213-4b261f3c3b90?crop=entropy&cs=srgb&fm=jpg&q=85";

const EASE = [0.16, 1, 0.3, 1];

export default function Home() {
  const { t } = useLang();
  const [cars, setCars] = useState([]);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const txtY = useTransform(scrollYProgress, [0, 1], [0, -60]);

  useEffect(() => {
    api.get("/cars").then((r) => setCars(r.data)).catch(() => {});
  }, []);

  const featured = cars.slice(0, 3);

  return (
    <main data-testid="home-page">
      {/* HERO */}
      <section ref={heroRef} className="relative min-h-screen flex items-center overflow-hidden bg-paper" data-testid="hero-section">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 w-full grid grid-cols-1 lg:grid-cols-12 gap-10 items-center pt-28 pb-16 lg:pt-20 lg:pb-8">
          <motion.div style={{ y: txtY }} className="lg:col-span-7 relative z-10">
            <p className="eyebrow mb-6 md:mb-8">{t("hero.eyebrow")}</p>
            <h1 className="display-title text-[17vw] sm:text-[13vw] lg:text-[7.2vw]">
              <MaskLine delay={0.15}>{t("hero.l1")}</MaskLine>
              <MaskLine delay={0.3}>
                <span className="italic text-rosso">{t("hero.l2")}</span>
              </MaskLine>
            </h1>
            <Reveal delay={0.55} className="mt-8 max-w-md">
              <p className="text-smoke text-base leading-relaxed">{t("hero.sub")}</p>
            </Reveal>
            <Reveal delay={0.7} className="mt-10 flex flex-wrap items-center gap-6">
              <Link to="/auto" className="btn-solid group" data-testid="hero-cta-inventory">
                {t("hero.cta1")}
                <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1.5" />
              </Link>
              <a href={waLink("Buongiorno, vorrei delle informazioni.")} target="_blank" rel="noopener noreferrer" className="btn-line" data-testid="hero-cta-whatsapp">
                {t("hero.cta2")}
                <ArrowUpRight size={15} />
              </a>
            </Reveal>
          </motion.div>

          <div className="lg:col-span-5 relative">
            <motion.div
              initial={{ clipPath: "inset(100% 0 0 0)" }}
              animate={{ clipPath: "inset(0% 0 0 0)" }}
              transition={{ duration: 1.3, delay: 0.45, ease: EASE }}
              className="relative aspect-[4/5] overflow-hidden"
            >
              <motion.img
                src={HERO_IMG}
                alt={t("hero.l1") + " " + t("hero.l2")}
                style={{ y: imgY }}
                initial={{ scale: 1.12 }}
                animate={{ scale: 1.02 }}
                transition={{ duration: 8, ease: "linear" }}
                className="w-full h-[112%] object-cover"
              />
            </motion.div>
            <div className="absolute -bottom-5 -left-5 w-40 h-40 border-b-2 border-r-2 border-rosso pointer-events-none" aria-hidden="true" />
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.4, duration: 0.8 }}
              className="absolute -bottom-12 right-0 font-mono text-[10px] tracking-[0.24em] uppercase text-fog"
            >
              Monzoro - Cusago (MI)
            </motion.p>
          </div>
        </div>
      </section>

      {/* MARQUEE */}
      <section className="bg-blu text-white py-5 overflow-hidden" aria-hidden="true">
        <div className="flex w-max animate-marquee items-center gap-10 whitespace-nowrap">
          {[0, 1].map((copy) => (
            <div key={copy} className="flex items-center gap-10" aria-hidden={copy === 1}>
              {["marquee.1", "marquee.2", "marquee.3", "marquee.4"].map((k) => (
                <span key={k} className="flex items-center gap-10">
                  <span className="font-display italic text-2xl md:text-3xl text-white/85">{t(k)}</span>
                  <Diamond size={11} className="text-rosso fill-rosso" />
                </span>
              ))}
            </div>
          ))}
        </div>
      </section>

      {/* FEATURED */}
      <section className="py-24 md:py-32" data-testid="featured-section">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10">
          <Reveal className="flex flex-wrap items-end justify-between gap-6 mb-12 md:mb-16">
            <div>
              <p className="eyebrow mb-4">{t("nav.inventory")}</p>
              <h2 className="display-title text-5xl md:text-7xl">{t("featured.title")}</h2>
            </div>
            <Link to="/auto" className="btn-line">
              {t("featured.all")}
              <ArrowRight size={15} />
            </Link>
          </Reveal>

          {featured.length === 0 ? (
            <p className="text-fog font-mono text-sm">{t("inventory.empty")}</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8">
              {featured[0] && (
                <Reveal className="md:col-span-7">
                  <CarCard car={featured[0]} tall />
                </Reveal>
              )}
              <div className="md:col-span-5 flex flex-col gap-6 md:gap-8">
                {featured.slice(1).map((c, i) => (
                  <Reveal key={c.id} delay={0.1 + i * 0.1}>
                    <CarCard car={c} />
                  </Reveal>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* SHOWROOM */}
      <section id="showroom" className="bg-paper2 py-24 md:py-32 scroll-mt-20" data-testid="showroom-section">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <Reveal className="lg:col-span-6 relative">
            <div className="aspect-[16/11] overflow-hidden">
              <img src={SHOWROOM_IMG} alt={t("showroom.title")} loading="lazy" className="w-full h-full object-cover hover:scale-[1.04] transition-transform duration-[900ms] ease-[cubic-bezier(0.16,1,0.3,1)]" />
            </div>
            <div className="hidden md:block absolute -bottom-10 -right-6 w-56 aspect-[4/3] overflow-hidden border-[6px] border-paper2">
              <img src={DETAIL_IMG} alt="" loading="lazy" className="w-full h-full object-cover" />
            </div>
          </Reveal>
          <Reveal delay={0.15} className="lg:col-span-5 lg:col-start-8">
            <p className="eyebrow mb-5">Monzoro - Cusago (MI)</p>
            <h2 className="display-title text-5xl md:text-6xl mb-8">{t("showroom.title")}</h2>
            <p className="text-smoke leading-relaxed mb-5">{t("showroom.p1")}</p>
            <p className="text-smoke leading-relaxed mb-8">{t("showroom.p2")}</p>
            <ul className="font-mono text-xs leading-6 text-fog space-y-0.5 border-t border-ink/10 pt-6">
              <li>{t("footer.showroom")} — Via G. Marconi 22, 20090 Monzoro - Cusago (MI)</li>
              <li>{t("footer.legal")} — Via Maffeo Bagarotti 16, 20152 Milano (MI)</li>
              <li>WhatsApp — +39 350 937 4737</li>
            </ul>
          </Reveal>
        </div>
      </section>

      {/* CTA */}
      <section id="contatti" className="bg-blu text-white py-24 md:py-36 scroll-mt-20" data-testid="contact-section">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 text-center">
          <Reveal>
            <h2 className="display-title text-5xl md:text-8xl">
              {t("cta.title")} <span className="italic text-rosso">{t("cta.title2")}</span>
            </h2>
            <p className="text-white/60 mt-8 max-w-md mx-auto leading-relaxed">{t("cta.sub")}</p>
            <a
              href={waLink("Buongiorno, vorrei delle informazioni.")}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-whatsapp-button"
              className="mt-12 inline-flex items-center gap-3 bg-whatsapp text-blu font-body text-sm uppercase tracking-[0.18em] px-10 py-5 hover:bg-white transition-colors duration-300"
            >
              {t("cta.btn")}
              <ArrowUpRight size={16} />
            </a>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
